import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import UserService from 'services/user.service';
import AuthServices from 'core/authentication/authService';
import KeycloakPubServiceInstance from 'services/keycloakpubsub.service';
import Loading from 'components/Loading';
import Animated from 'components/Animated';
import SliderMessage from 'components/SliderMessage';
import { sendUserDataToAnalytics } from 'utils/gtm';

const LoadUserInfo: React.FC = () => {
  const user = useSelector((state: any) => state.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCurrentUserError, setShowCurrentUserError] = useState(false);
  const [textCurrentUserError, setTextCurrentUserError] = useState('');

  useEffect(() => {
    const authService = new AuthServices();
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    let requested = false;
    const showError = (message: string, logout = true) => {
      if (cancelled) return;
      setTextCurrentUserError(message);
      setShowCurrentUserError(true);
      if (logout && !timeout) {
        timeout = setTimeout(() => { void authService.logout(); }, 5000);
      }
    };
    const handleUserDisabled = () => showError('Usuário sem permissão de acesso.');
    const handleServiceUnavailable = () => showError(
      'Serviço temporariamente indisponível. Tente novamente em instantes.', false,
    );
    const loadUser = async () => {
      if (requested || Object.entries(user).length) return;
      requested = true;
      try {
        const result = await new UserService().currentUser();
        if (cancelled) return;
        if (!result?.data?.enabled || result.data.deleted) {
          showError('Usuário desabilitado, favor entrar em contato com o administrador.');
          return;
        }
        if (result.data.pending) {
          showError('Usuário com cadastro incompleto.');
          return;
        }
        dispatch({ type: 'SET_USER_DATA', data: result.data });
        void new UserService().updateLastLogin()?.catch(() => {});
        if (location.pathname === '/') {
          sendUserDataToAnalytics('login', result.data);
          navigate('/cotacoes-dashboard');
        }
      } catch (err: any) {
        if (err?.response?.status === 503) {
          handleServiceUnavailable();
          return;
        }
        showError(err?.response?.status === 401
          ? 'Usuário não foi encontrado no portal. Favor entrar em contato com a administração para recadastro'
          : 'Houve um erro e não foi possível logar no sistema');
      }
    };
    window.addEventListener('portal:user-disabled', handleUserDisabled);
    window.addEventListener('portal:service-unavailable', handleServiceUnavailable);
    KeycloakPubServiceInstance.subscribe(loadUser);
    return () => {
      cancelled = true;
      if (timeout) clearTimeout(timeout);
      KeycloakPubServiceInstance.unbsubscribe(loadUser);
      window.removeEventListener('portal:user-disabled', handleUserDisabled);
      window.removeEventListener('portal:service-unavailable', handleServiceUnavailable);
    };
    // Profile loading runs once per mount; subsequent selections remain in Redux.
  }, []);

  return (
    <>
      <SliderMessage show={showCurrentUserError} message={textCurrentUserError} />
      <Animated
        show={!Object.entries(user).length}
        animationIn="none" animationInDelay="faster"
        animationOut="fadeOut" animationOutDelay="delay-1s"
        className="fixed top-0 right-0 bottom-0 left-0 bg-white d-flex align-center justify-center z-999"
      >
        <Loading size={45} />
      </Animated>
    </>
  );
};

export default LoadUserInfo;
